import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle, Loader2, X, Eye, Download, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import DocumentTypesService from '@/api/documentTypes.service';
import DocumentUploadsService from '@/api/documentUploads.service';
import { cn } from '@/lib/utils';

interface DocumentUploadProps {
  documentType: 'invoice' | 'receipt' | 'bill' | 'quotation';
  businessId: number;
  onExtractionComplete?: (extractedData: any) => void;
  onUploadComplete?: (documentUploadId: number) => void;
  className?: string;
  disabled?: boolean;
  acceptedFileTypes?: string;
  maxFileSize?: number; // in MB
}

interface UploadedDocument {
  id: number;
  fileName: string;
  fileUrl: string;
  processingStatus: 'pending' | 'processing' | 'completed' | 'failed';
  extractedData?: any;
  confidenceScore?: number;
  errorMessage?: string;
}

export function DocumentUpload({
  documentType,
  businessId,
  onExtractionComplete,
  onUploadComplete,
  className,
  disabled = false,
  acceptedFileTypes = '.pdf,.png,.jpg,.jpeg',
  maxFileSize = 10
}: DocumentUploadProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedDocument, setUploadedDocument] = useState<UploadedDocument | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState<'pending' | 'processing' | 'completed' | 'failed'>('pending');
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (disabled) return;
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files);
    }
  }, [disabled]);

  const handleFileSelection = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(Array.from(files));
    }
  }, []);

  const validateFile = (file: File) => {
    // Check file size
    if (file.size > maxFileSize * 1024 * 1024) {
      toast({
        title: "Invalid File",
        description: `File size must be less than ${maxFileSize}MB`,
        variant: "destructive",
      });
      return false;
    }

    // Check file type
    const allowedTypes = acceptedFileTypes.split(',').map(type => type.trim());
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedTypes.includes(fileExtension)) {
      toast({
        title: "Invalid File",
        description: `File type not supported. Allowed types: ${acceptedFileTypes}`,
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleFileUpload = async (files: File[]) => {
    if (files.length === 0) return;

    const file = files[0];
    
    // Validate file
    if (!validateFile(file)) return;

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('businessId', businessId.toString());
      formData.append('documentType', documentType);
      formData.append('uploadMethod', 'web');

      const uploadResponse = await DocumentUploadsService.uploadDocument(formData);
      
      if (uploadResponse.data.result === 'success') {
        const documentUploadId = uploadResponse.data.documentUpload.id;
        setUploadedDocument({
          id: documentUploadId,
          fileName: file.name,
          fileUrl: uploadResponse.data.fileUrl,
          processingStatus: 'pending'
        });
        
        setUploadProgress(100);
        
        // Start monitoring processing status
        monitorProcessingStatus(documentUploadId);
        
        toast({
          title: "Success",
          description: "File uploaded successfully. Processing started.",
        });
      } else {
        throw new Error(uploadResponse.data.message || 'Upload failed');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      setError(error.message || 'Failed to upload file');
      toast({
        title: "Error",
        description: error.message || "Failed to upload file",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const monitorProcessingStatus = useCallback(async (documentUploadId: number) => {
    setIsProcessing(true);
    setProcessingStatus('processing');
    
    const maxAttempts = 30; // 5 minutes with 10-second intervals
    let attempts = 0;
    
    const checkStatus = async () => {
      try {
        attempts++;
        
        const statusResponse = await DocumentUploadsService.getDocumentProcessingStatus(documentUploadId);
        
        if (statusResponse.data.result === 'success') {
          const status = statusResponse.data.status;
          const progress = statusResponse.data.progress || 0;
          
          setProcessingProgress(progress);
          
          if (status === 'completed') {
            setProcessingStatus('completed');
            setIsProcessing(false);
            
            // Fetch extraction results
            await fetchExtractionResults(documentUploadId);
            
            toast({
              title: "Processing Complete",
              description: "Document has been processed successfully",
            });
            
            return;
          } else if (status === 'failed') {
            setProcessingStatus('failed');
            setIsProcessing(false);
            setError('Document processing failed');
            
            toast({
              title: "Processing Failed",
              description: "Failed to process the document",
              variant: "destructive",
            });
            
            return;
          }
        }
        
        // Continue monitoring if not completed and haven't exceeded max attempts
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 10000); // Check every 10 seconds
        } else {
          setProcessingStatus('failed');
          setIsProcessing(false);
          setError('Processing timeout - please try again');
          
          toast({
            title: "Processing Timeout",
            description: "Document processing is taking longer than expected",
            variant: "destructive",
          });
        }
      } catch (error: any) {
        console.error('Status check error:', error);
        
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 10000); // Retry after 10 seconds
        } else {
          setProcessingStatus('failed');
          setIsProcessing(false);
          setError('Failed to check processing status');
        }
      }
    };
    
    // Start checking status after a short delay
    setTimeout(checkStatus, 2000);
  }, [toast]);

  const fetchExtractionResults = async (documentUploadId: number) => {
    try {
      const extractionResponse = await DocumentUploadsService.getDocumentExtraction(documentUploadId);
      
      if (extractionResponse.data.result === 'success') {
        const extractionData = extractionResponse.data.extraction;
        setUploadedDocument(prev => prev ? {
          ...prev,
          extractedData: extractionData.structuredData,
          confidenceScore: extractionData.confidenceScore
        } : null);

        onExtractionComplete?.(extractionData.structuredData || {});

        toast({
          title: "Processing Complete",
          description: `Document processed with ${Math.round((extractionData.confidenceScore || 0) * 100)}% confidence`,
        });
      }
    } catch (error: any) {
      console.error('Extraction fetch error:', error);
      toast({
        title: "Warning",
        description: "Failed to fetch extraction results",
        variant: "destructive",
      });
    }
  };

  const handleRemoveDocument = () => {
    setUploadedDocument(null);
    setUploadProgress(0);
    setProcessingProgress(0);
    setIsProcessing(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Loader2 className="h-4 w-4 animate-spin text-amber-500" />;
      case 'processing':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      pending: { variant: 'secondary', text: 'Pending' },
      processing: { variant: 'default', text: 'Processing' },
      completed: { variant: 'default', text: 'Completed' },
      failed: { variant: 'destructive', text: 'Failed' }
    };

    const config = variants[status] || { variant: 'secondary', text: status };
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        const documentTypesResponse = await DocumentTypesService.getDocumentTypes();
        if (documentTypesResponse.data.result === 'success') {
          // Document types fetched successfully - can be used if needed
        }
      } catch (error) {
        console.error('Error fetching document types:', error);
        toast({
          title: "Error",
          description: "Failed to fetch document types",
          variant: "destructive",
        });
      }
    };

    fetchDocumentTypes();
  }, [toast]);

  if (uploadedDocument) {
    return (
      <Card className={cn("w-full", className)}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium">Uploaded Document</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemoveDocument}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {getStatusIcon(processingStatus)}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{uploadedDocument.fileName}</p>
              <div className="flex items-center gap-2 mt-1">
                {getStatusBadge(processingStatus)}
                {uploadedDocument.confidenceScore && (
                  <Badge variant="outline">
                    {Math.round(uploadedDocument.confidenceScore * 100)}% confidence
                  </Badge>
                )}
              </div>
            </div>
            {uploadedDocument.fileUrl && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(uploadedDocument.fileUrl, '_blank')}
              >
                <Eye className="h-4 w-4 mr-1" />
                View
              </Button>
            )}
          </div>

          {isProcessing && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Processing document...</span>
                <span>{processingProgress}%</span>
              </div>
              <Progress value={processingProgress} className="h-2" />
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {uploadedDocument.extractedData && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm font-medium text-green-800 mb-2">Extraction Results</p>
              <div className="text-xs text-green-700 space-y-1">
                {uploadedDocument.extractedData.vendorName && (
                  <div>Vendor: {uploadedDocument.extractedData.vendorName}</div>
                )}
                {uploadedDocument.extractedData.totalAmount && (
                  <div>Amount: {uploadedDocument.extractedData.currency || '$'}{uploadedDocument.extractedData.totalAmount}</div>
                )}
                {uploadedDocument.extractedData.invoiceDate && (
                  <div>Date: {uploadedDocument.extractedData.invoiceDate}</div>
                )}
                {uploadedDocument.extractedData.invoiceNumber && (
                  <div>Number: {uploadedDocument.extractedData.invoiceNumber}</div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("w-full", className)}>
      <CardContent className="p-6">
        <div
          className={cn(
            "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
            isDragOver ? "border-primary bg-primary/5" : "border-gray-300",
            disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-primary hover:bg-primary/5"
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => {
            if (!disabled) {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = acceptedFileTypes;
              input.onchange = (e) => {
                const target = e.target as HTMLInputElement;
                if (target.files && target.files.length > 0) {
                  handleFileUpload(Array.from(target.files));
                }
              };
              input.click();
            }
          }}
        >
          {isUploading ? (
            <div className="space-y-4">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <div className="space-y-2">
                <p className="text-sm font-medium">Uploading document...</p>
                <Progress value={uploadProgress} className="h-2" />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Upload className="h-8 w-8 mx-auto text-gray-400" />
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Upload {documentType} document for AI processing
                </p>
                <p className="text-xs text-gray-500">
                  Drag and drop or click to select • {acceptedFileTypes} • Max {maxFileSize}MB
                </p>
              </div>
              <Button variant="outline" size="sm" disabled={disabled}>
                Select File
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
} 